/**
 * What the AI is doing in one chat (POST /v1/chats/ai-session → {session,
 * profile}): the brain writes it, the CRM only reads. The mappers are ports of
 * the CRM's ai-layer.js, for the side column's AI reasoning, Fan summary by AI
 * and the note suggestions.
 */
import { useEffect, useState } from 'react'
import { crmFetch, dataOf } from '../chat/api'

type Raw = Record<string, unknown>

export interface AiActivity {
  ts?: string | number | null
  reasoning?: string | null
  receptiveness?: string | null
  vibe?: string | null
}

export interface AiSession {
  status?: string | null
  vibe?: string | null
  last_message_at?: string | null
  ai_message_count?: number | null
  fan_message_count?: number | null
  avg_fan_response_seconds?: number | null
  ai_activity?: AiActivity[] | null
}

/** The brain's profile of the fan (fields as the brain names them). */
export type AiProfile = Raw

export interface AiInsight {
  session: AiSession | null
  profile: AiProfile | null
  loaded: boolean
}

const EMPTY: AiInsight = { session: null, profile: null, loaded: false }
const POLL_MS = 12_000

/** The open chat's AI session and profile, re-read every 12 s while the tab is visible (ai-layer.js). */
export function useAiSession(accountId: string, fanId: string, enabled = true): AiInsight {
  const [insight, setInsight] = useState<AiInsight>(EMPTY)
  useEffect(() => {
    setInsight(EMPTY)
    if (!enabled) return
    let alive = true
    const load = () =>
      crmFetch('/v1/chats/ai-session', { accountId, local: true, body: { fan_id: fanId } })
        .then((body) => {
          if (!alive) return
          const d = (dataOf(body) || {}) as { session?: AiSession | null; profile?: AiProfile | null }
          setInsight({ session: d.session ?? null, profile: d.profile ?? null, loaded: true })
        })
        .catch(() => alive && setInsight((prev) => ({ ...prev, loaded: true })))
    void load()
    const timer = window.setInterval(() => {
      if (!document.hidden) void load()
    }, POLL_MS)
    return () => {
      alive = false
      window.clearInterval(timer)
    }
  }, [accountId, fanId, enabled])
  return insight
}

/** Profile facts as text: plain text, real lists, or lists serialised into text (JSON or Python's). */
export function profileText(value: unknown): string {
  if (value == null) return ''
  if (Array.isArray(value)) return value.map(profileText).filter(Boolean).join(', ')
  if (typeof value === 'object') {
    return Object.entries(value as Raw)
      .map(([k, v]) => {
        const text = profileText(v)
        return text ? `${k}: ${text}` : ''
      })
      .filter(Boolean)
      .join(', ')
  }
  const text = String(value).trim()
  if (!/^\[[\s\S]*\]$/.test(text)) return text
  try {
    const parsed: unknown = JSON.parse(text)
    if (Array.isArray(parsed)) return profileText(parsed)
  } catch {
    /* not JSON: strip the quotes ourselves */
  }
  const inner = text.slice(1, -1).trim()
  if (!inner) return ''
  return (inner.match(/"(?:[^"\\]|\\.)*"|'(?:[^'\\]|\\.)*'|[^,]+/g) || [])
    .map((item) => item.trim().replace(/^(["'])([\s\S]*)\1$/, '$2').trim())
    .filter(Boolean)
    .join(', ')
}

export type ReceptivenessLevel = 'high' | 'medium' | 'low' | 'unknown'

/** The brain's free-text receptiveness → a level; `key` names the copy, `raw` is shown when no copy fits. */
export function receptiveness(value: unknown): { level: ReceptivenessLevel; key: string | null; raw: string } {
  const raw = String(value ?? '').trim()
  if (!raw) return { level: 'unknown', key: 'unknown', raw }
  const s = raw.toLowerCase()
  const has = (...words: string[]) => words.some((w) => s.includes(w))
  if (has('very open', 'very receptive', 'eager', 'enthusiastic', 'hot', 'horny')) return { level: 'high', key: 'veryHigh', raw }
  if (has('open', 'receptive', 'warm', 'engaged', 'interested', 'playful', 'flirty', 'positive')) return { level: 'high', key: 'high', raw }
  if (has('closed', 'cold', 'resistant', 'guarded', 'distant', 'annoyed', 'negative', 'shut')) return { level: 'low', key: 'low', raw }
  if (has('neutral', 'unsure', 'mixed', 'cautious', 'testing', 'lukewarm')) return { level: 'medium', key: 'medium', raw }
  return { level: 'medium', key: null, raw }
}

const VIBES: Record<string, { key: string; emoji: string }> = {
  'warming up': { key: 'warming', emoji: '🌤️' },
  'getting flirty': { key: 'flirty', emoji: '😏' },
  'building to something': { key: 'building', emoji: '🔥' },
  'cooling off': { key: 'cooling', emoji: '🧊' },
}

/** A known vibe has copy (`key`); any other is shown as the brain wrote it. */
export function vibe(value: unknown): { key: string | null; emoji: string; raw: string } | null {
  const raw = String(value ?? '').trim()
  if (!raw) return null
  const hit = VIBES[raw.toLowerCase()]
  return hit ? { ...hit, raw } : { key: null, emoji: '💬', raw }
}

/** Fan replies per AI message, as a percent capped at 100. */
export function replyRate(fanCount: unknown, aiCount: unknown): number | null {
  const fan = Number(fanCount) || 0
  const ai = Number(aiCount) || 0
  if (ai <= 0) return null
  return Math.max(0, Math.min(100, Math.round((fan / ai) * 100)))
}

/** 45s, 2m, 2m 5s, 1h 3m; nothing → —. */
export function durationLabel(seconds: unknown): string {
  const s = Number(seconds)
  if (!Number.isFinite(s) || s <= 0) return '—'
  if (s < 60) return `${Math.round(s)}s`
  const m = Math.floor(s / 60)
  if (m < 60) {
    const rem = Math.round(s % 60)
    return rem ? `${m}m ${rem}s` : `${m}m`
  }
  const h = Math.floor(m / 60)
  return m % 60 ? `${h}h ${m % 60}m` : `${h}h`
}

/** One-tap Fan note lines from what the AI learned (ai-layer.js aiNoteSuggestions). */
export function noteSuggestions(profile: AiProfile | null | undefined): { kind: string; label: string; template: string }[] {
  if (!profile || typeof profile !== 'object') return []
  const out: { kind: string; label: string; template: string }[] = []
  const seen = new Set<string>()
  const push = (kind: string, fact: unknown) => {
    const text = profileText(fact).replace(/\s+/g, ' ').trim()
    if (!text) return
    const template = `${kind}: ${text}`
    if (seen.has(template.toLowerCase())) return
    seen.add(template.toLowerCase())
    out.push({ kind, label: text.length > 48 ? `${text.slice(0, 47)}…` : text, template })
  }
  push('Preference', profile.hobbies_and_interests)
  if (profile.fan_type) push('Preference', `${profileText(profile.fan_type)} type`)
  push('Preference', profile.inside_jokes_and_slang)
  if (profile.names_fan_calls_model) push('Preference', `calls me ${profileText(profile.names_fan_calls_model)}`)
  push('Preference', profile.sex_fetishes_brief_summary)
  push('Promise', profile.chat_recommendation)
  return out.slice(0, 6)
}

/** The newest reasoning (the brain's ring buffer is oldest first). */
export function latestActivity(session: AiSession | null): AiActivity | null {
  const list = session?.ai_activity
  return Array.isArray(list) && list.length ? list[list.length - 1] ?? null : null
}
